import Plot from "react-plotly.js";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";
import { useCallback, useMemo } from "react";
import { Config, Layout, PlotData, PlotMouseEvent } from "plotly.js";
import "./DefectInsights.css";

type CommonDefectsGraphProps = {
    className?: string;
    data: Partial<PlotData>[]; 
    filterValues: string[] | undefined;
    legendText?: string;
    loading: boolean;
    onClick?: (event: PlotMouseEvent) => void;
    maxItems?: number;
};

export const CommonDefectsGraph = (props: CommonDefectsGraphProps) => {
    const {
        className = "defect-insight-graph",
        data,
        filterValues,
        legendText,
        loading,
        onClick,
        maxItems,
    } = props;

    const { t } = useTranslation("locale");

    const limitedData = useMemo(() => {
        if (!maxItems || !data?.length) {
            return data;
        }

        return data.map((trace) => ({
            ...trace,
            x: Array.isArray(trace.x) ? trace.x.slice(0, maxItems) : trace.x,
            y: Array.isArray(trace.y) ? trace.y.slice(0, maxItems) : trace.y,
        }));
    }, [data, maxItems]);

    const getDTickValue = useCallback(() => {
        if (data.length && data[0].x) {
            const max = data[0].x[0];

            if (typeof max === "number" && max < 11) {
                return 1;
            }
        }

        return undefined;
    }, [data]);

    const layout = useMemo(() => {
        const graphLayout: Partial<Layout> = {
            autosize: true,
            xaxis: {
                showgrid: true,
                gridcolor: "#B1E1E4",
                zerolinecolor: "#B1E1E4",
                fixedrange: true,
                tickfont: {
                    color: "#555",
                    size: 13,
                },
                dtick: getDTickValue(),
                title: t("defectInsights.numberOfDefects"),
                titlefont: {
                    color: "#555",
                    size: 13,
                },
            },
            yaxis: {
                showgrid: false,
                automargin: true,
                autorange: "reversed",
                categoryarray: filterValues?.length ? filterValues : ["All"],
                categoryorder: "total descending",
                fixedrange: true,
                zerolinecolor: "#B1E1E4",
                tickfont: {
                    color: "#555",
                    size: 13,
                },
                titlefont: {
                    color: "#555",
                    size: 13,
                },
                ticklen: 10,
                tickcolor: "rgba(0,0,0,0)",
            },
            margin: {
                pad: 0,
                l: 60,
                t: 20,
                b: 60,
                r: 0,
            },
            font: {
                family: "Open Sans",
                color: "#555",
                size: 12,
            },
            showlegend: !!legendText,
            legend: {
                title: {
                    font: {
                        color: "#555",
                        size: 13,
                    },
                    text: legendText,
                },
            },
            plot_bgcolor: "#F4F8FB",
            hovermode: "closest",
            hoverlabel: {
                bgcolor: "#1C1940",
                bordercolor: "#1C1940",
                font: {
                    color: "#FFF",
                    size: 13,
                },
            },
            barmode: filterValues?.length ? "stack" : undefined,
        };

        return graphLayout;
    }, [data]);

    const config = useMemo(() => {
        const graphConfig: Partial<Config> = {
            displayModeBar: true,
            responsive: true,
            displaylogo: false,
            scrollZoom: false,
            showTips: false,
            modeBarButtonsToRemove: [
                "pan2d",
                "zoom2d",
                "autoScale2d",
                "lasso2d",
                "select2d",
            ],
        };

        return graphConfig;
    }, []);

    if (loading) {
        return (
            <div className="flex justify-items-center align-items-center">
                <ProgressSpinner />
            </div>
        );
    }

    if (!data?.length || !data[0]?.x?.length || !data[0]?.y?.length) {
        return (
            <div className="flex justify-content-center">
                <p>{t("defectsGraph.noDefectsDataAvailable")}</p>
            </div>
        );
    }

    return (
        <Plot
            useResizeHandler={true}
            data={limitedData}
            layout={layout}
            config={config}
            className={className}
            onClick={onClick}
        />
    );
};
